import { useEffect, useMemo, useState } from "react";
import { financeService } from "../../services/financeService";
import Icon from "../Common/Icon";

const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2,
  });

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Randevu tahsilat modalı. `row`: { appointmentId, customerName, total, remaining }.
 * Açılışta checkout bağlamını (kalan + aktif kampanyalar + sadakat programı) çeker;
 * kampanya ve/veya puan seçilerek tahsilatta indirim uygulanır. İndirimler borcu
 * kapatır ama kasaya yansımaz. Kaydettikten sonra onSaved() çağrılır.
 */
export default function PaymentCollectModal({ row, onClose, onSaved, showToast }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("CASH");
  const [campaignId, setCampaignId] = useState("");
  const [pointsInput, setPointsInput] = useState("");
  const [cashInput, setCashInput] = useState("");
  const [cashTouched, setCashTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createInvoiceChecked, setCreateInvoiceChecked] = useState(false);

  useEffect(() => {
    financeService
      .checkout(row.appointmentId)
      .then((res) => setCtx(res.data?.data ?? null))
      .catch(() => showToast("Tahsilat bilgileri yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, [row.appointmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remaining = Number(ctx?.remaining ?? row.remaining ?? 0);
  const loyalty = ctx?.loyalty;
  const campaigns = useMemo(() => ctx?.campaigns ?? [], [ctx]);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === campaignId) || null,
    [campaigns, campaignId]
  );

  // Kampanya indirimi (backend ile aynı kural)
  const campaignDiscount = useMemo(() => {
    const c = selectedCampaign;
    if (!c) return 0;
    if (c.minPurchase != null && remaining < Number(c.minPurchase)) return 0;
    let d = c.discountType === "PERCENTAGE" ? (remaining * Number(c.discountValue)) / 100 : Number(c.discountValue);
    return round2(Math.min(d, remaining));
  }, [selectedCampaign, remaining]);

  const valuePerPoint = Number(loyalty?.redeemValuePerPoint ?? 1);
  // Puan indirimi kalan (kampanya sonrası) tutarı aşamaz.
  const maxRedeemByAmount = Math.max(0, Math.floor((remaining - campaignDiscount) / (valuePerPoint || 1)));
  const maxRedeem = Math.min(Number(loyalty?.customerPoints ?? 0), maxRedeemByAmount);
  const redeemPoints = Math.max(0, Math.min(Number(pointsInput || 0), maxRedeem));
  const loyaltyDiscount = round2(redeemPoints * valuePerPoint);

  const totalDiscount = round2(campaignDiscount + loyaltyDiscount);
  const suggestedCash = round2(Math.max(0, remaining - totalDiscount));
  const cash = cashTouched ? Number(cashInput || 0) : suggestedCash;
  const settled = round2(cash + totalDiscount);

  const minRedeem = Number(loyalty?.minRedeemPoints ?? 0);
  const pointsError =
    redeemPoints > 0 && minRedeem > 0 && redeemPoints < minRedeem
      ? `Puan harcamak için en az ${minRedeem} puan gerekli.`
      : null;

  const save = async (e) => {
    e.preventDefault();
    if (pointsError) {
      showToast(pointsError, "error");
      return;
    }
    if (settled <= 0) {
      showToast("Tahsil edilecek tutar veya indirim girilmeli.", "error");
      return;
    }
    if (settled > remaining + 0.001) {
      showToast(`Tahsilat + indirim kalandan fazla olamaz (${fmtTL(remaining)}).`, "error");
      return;
    }
    setSaving(true);
    try {
      // Fatura, tahsilatla aynı işlemde backend'de kesilir: ayrı bir istek
      // olsaydı ödeme yazılıp fatura yazılamadığında ikisi ayrışabilirdi.
      // Belge tutarı bu ödemede kapanan kısımdır (nakit + indirim).
      await financeService.collectPayment(row.appointmentId, {
        amount: round2(cash),
        paymentMethod: method,
        transactionDate: null,
        campaignId: campaignId || null,
        redeemPoints: redeemPoints > 0 ? redeemPoints : null,
        createInvoice: createInvoiceChecked,
      });

      showToast(
        createInvoiceChecked ? "Tahsilat kaydedildi ve faturası oluşturuldu." : "Tahsilat kaydedildi.",
        "success"
      );
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-md w-full overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low shrink-0">
          <h3 className="font-bold text-lg text-on-surface flex items-center gap-2">
            <Icon name="payments" className="text-primary !text-[22px]" /> Tahsilat — {ctx?.customerName ?? row.customerName}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full text-on-surface-variant hover:text-primary">
            <Icon name="close" className="!text-[22px]" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-on-surface-variant text-sm">Yükleniyor…</div>
        ) : (
          <form onSubmit={save} className="p-6 space-y-4 text-sm overflow-y-auto">
            <div className="flex items-center justify-between text-xs bg-surface-container-low rounded-xl px-3 py-2">
              <span className="text-on-surface-variant">Toplam: <strong className="text-on-surface">{fmtTL(ctx?.total ?? row.total)}</strong></span>
              <span className="text-on-surface-variant">Kalan: <strong className="text-error">{fmtTL(remaining)}</strong></span>
            </div>

            {/* Aktif Kampanyalar */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold mb-2 text-on-surface-variant">
                <Icon name="local_offer" className="!text-[16px] text-primary" /> Aktif Kampanyalar
              </label>
              {campaigns.length === 0 ? (
                <p className="text-xs text-on-surface-variant bg-surface-container-low rounded-xl px-3 py-2">
                  Şu an uygulanabilir aktif kampanya yok.
                </p>
              ) : (
                <div className="space-y-1.5">
                  <CampaignOption
                    selected={campaignId === ""}
                    onSelect={() => setCampaignId("")}
                    label="Kampanya uygulama"
                  />
                  {campaigns.map((c) => {
                    const belowMin = c.minPurchase != null && remaining < Number(c.minPurchase);
                    return (
                      <CampaignOption
                        key={c.id}
                        selected={campaignId === c.id}
                        disabled={belowMin}
                        onSelect={() => setCampaignId(c.id)}
                        label={c.name}
                        hint={
                          belowMin
                            ? `Min. ${fmtTL(c.minPurchase)} gerekli`
                            : c.discountType === "PERCENTAGE"
                            ? `%${Number(c.discountValue)} indirim`
                            : `${fmtTL(c.discountValue)} indirim`
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Aktif Sadakat Programı */}
            {loyalty?.active && (
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold mb-2 text-on-surface-variant">
                  <Icon name="loyalty" className="!text-[16px] text-tier-gold" /> Aktif Sadakat Programı
                </label>
                <div className="bg-tier-gold/5 border border-tier-gold/30 rounded-xl px-3 py-2.5 space-y-2">
                  <div className="flex items-center justify-between text-xs text-on-surface-variant">
                    <span>Müşteri puanı: <strong className="text-on-surface">{loyalty.customerPoints}</strong></span>
                    <span>1 puan = {fmtTL(valuePerPoint)}</span>
                  </div>
                  {maxRedeem > 0 ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max={maxRedeem}
                        value={pointsInput}
                        onChange={(e) => setPointsInput(e.target.value)}
                        placeholder="Kullanılacak puan"
                        className="flex-1 border border-outline-variant rounded-lg px-2.5 py-1.5 text-sm bg-white outline-none focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={() => setPointsInput(String(maxRedeem))}
                        className="text-xs font-semibold text-primary hover:bg-primary/10 rounded-lg px-2.5 py-1.5 transition whitespace-nowrap"
                      >
                        Max ({maxRedeem})
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-on-surface-variant">
                      {Number(loyalty.customerPoints) < minRedeem
                        ? `Puan harcamak için en az ${minRedeem} puan gerekli.`
                        : "Bu tahsilat için kullanılabilir puan yok."}
                    </p>
                  )}
                  {pointsError && <p className="text-xs text-error">{pointsError}</p>}
                </div>
              </div>
            )}

            {/* Tutar özeti */}
            <div className="rounded-xl border border-outline-variant divide-y divide-outline-variant text-xs">
              <Row label="Kalan tutar" value={fmtTL(remaining)} />
              {campaignDiscount > 0 && <Row label="Kampanya indirimi" value={`− ${fmtTL(campaignDiscount)}`} valueClass="text-primary" />}
              {loyaltyDiscount > 0 && <Row label={`Sadakat indirimi (${redeemPoints} puan)`} value={`− ${fmtTL(loyaltyDiscount)}`} valueClass="text-tier-gold" />}
              <Row label="Toplam indirim" value={`− ${fmtTL(totalDiscount)}`} valueClass="text-success font-semibold" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tahsil Edilecek (₺)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={cashTouched ? cashInput : suggestedCash}
                  onChange={(e) => {
                    setCashTouched(true);
                    setCashInput(e.target.value);
                  }}
                  className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Yöntem</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full border border-outline-variant rounded-xl px-3 py-2 bg-white outline-none focus:border-primary"
                >
                  <option value="CASH">Nakit</option>
                  <option value="CARD">Kart</option>
                  <option value="TRANSFER">Havale</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs bg-primary/5 rounded-xl px-3 py-2">
              <span className="text-on-surface-variant">Kapanacak toplam</span>
              <strong className="text-on-surface">{fmtTL(settled)}</strong>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="createInvoiceChecked"
                checked={createInvoiceChecked}
                onChange={(e) => setCreateInvoiceChecked(e.target.checked)}
                className="w-4 h-4 text-primary border-outline-variant rounded focus:ring-primary"
              />
              <label htmlFor="createInvoiceChecked" className="text-xs font-semibold text-on-surface">
                Bu Randevu için Resmi Fatura Kes / Al
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
                İptal
              </button>
              <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50">
                {saving ? "Kaydediliyor…" : "Tahsil Et"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CampaignOption({ selected, disabled, onSelect, label, hint }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-left transition ${
        disabled
          ? "border-outline-variant opacity-50 cursor-not-allowed"
          : selected
          ? "border-primary bg-primary/5"
          : "border-outline-variant hover:bg-surface-container-high"
      }`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon
          name={selected ? "radio_button_checked" : "radio_button_unchecked"}
          className={`!text-[18px] ${selected ? "text-primary" : "text-on-surface-variant"}`}
        />
        <span className="truncate text-on-surface">{label}</span>
      </span>
      {hint && <span className="text-[11px] text-on-surface-variant whitespace-nowrap">{hint}</span>}
    </button>
  );
}

function Row({ label, value, valueClass = "text-on-surface" }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className="text-on-surface-variant">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
